import { useState, useEffect, lazy, Suspense } from 'react';
import { motion } from 'framer-motion';
import { HeroSection } from './components/sections/HeroSection';
import { Footer } from './components/common/Footer';
import { Header } from './components/common/Header';

// Code Splitting - 페이지 컴포넌트를 lazy loading으로 변경
const MenuPage = lazy(() => import('./components/MenuPage').then(module => ({ default: module.MenuPage })));
const FacilityPage = lazy(() => import('./components/FacilityPage').then(module => ({ default: module.FacilityPage })));
const NoticePageWithAPI = lazy(() => import('./components/NoticePageWithAPI').then(module => ({ default: module.NoticePageWithAPI })));
const BusinessPage = lazy(() => import('./components/BusinessPage').then(module => ({ default: module.BusinessPage })));
const TermsPage = lazy(() => import('./components/TermsPage').then(module => ({ default: module.TermsPage })));
const PrivacyPage = lazy(() => import('./components/PrivacyPage').then(module => ({ default: module.PrivacyPage })));

// Import remaining sections that need to be created
import { FacilitySection } from './components/sections/FacilitySection';
import { MenuSection } from './components/sections/MenuSection';
import { UsageSection } from './components/sections/UsageSection';
import { NoticeSectionWithAPI } from './components/sections/NoticeSectionWithAPI';
import { BusinessSection } from './components/sections/BusinessSection';
import { ContactSection } from './components/sections/ContactSection';

// 로딩 컴포넌트
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      <p className="mt-4 text-gray-600">페이지를 불러오는 중...</p>
    </div>
  </div>
);

export default function App() {
  const [currentPage, setCurrentPage] = useState<'home' | 'menu' | 'facility' | 'notice' | 'business' | 'terms' | 'privacy'>('home');
  const [inquiryType, setInquiryType] = useState<string>('business');

  // 페이지 로드 시 항상 맨 위로 스크롤
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // 네이버 지도 API 로드 (통합 버전)
  useEffect(() => {
    const loadNaverMapsAPI = () => {
      // 이미 로드되었는지 확인
      if ((window as any).naver?.maps) {
        console.log('네이버 지도 API가 이미 로드되어 있습니다.');
        return;
      }

      // 기존 스크립트가 있는지 확인
      const existingScript = document.querySelector('script[src*="openapi.map.naver.com"]');
      if (existingScript) {
        console.log('네이버 지도 스크립트가 이미 존재합니다.');
        return;
      }

      const naverClientId = import.meta.env.VITE_NAVER_MAP_CLIENT_ID;
      
      if (!naverClientId || naverClientId === 'YOUR_NAVER_CLIENT_ID_HERE') {
        console.error('⚠️ 네이버 맵 Client ID가 설정되지 않았습니다.');
        console.error('👉 .env 파일에 VITE_NAVER_MAP_CLIENT_ID를 설정해주세요.');
        return;
      }

      // 도메인 확인 및 안내
      const currentDomain = window.location.hostname;
      const isProduction = currentDomain === 'kidscafe.modouarena.com' || currentDomain === 'www.kidscafe.modouarena.com';
      const isDevelopment = currentDomain === 'localhost' || currentDomain === '127.0.0.1';
      
      console.log('🌍 현재 도메인:', currentDomain);
      console.log('📍 환경:', isProduction ? '프로덕션' : isDevelopment ? '개발' : '기타');
      
      // 전역 콜백 함수들을 먼저 정의
      // 성공 콜백
      (window as any).initNaverMap = () => {
        console.log('✅ 네이버 지도 API 로드 성공');
        console.log('📍 네이버 지도 버전:', (window as any).naver?.maps?.VERSION);
        // 전역 이벤트 발생시켜 컴포넌트에 알림
        window.dispatchEvent(new Event('naver-maps-loaded'));
      };
      
      // 인증 실패 콜백
      (window as any).navermap_authFailure = () => {
        console.error('❌ 네이버 지도 API 인증 실패');
        console.error('도메인:', currentDomain);
        
        if (isProduction) {
          console.error('🔧 해결 방법:');
          console.error('1. 네이버 클라우드 플랫폼 콘솔 접속: https://console.ncloud.com');
          console.error('2. Products & Services > Application > Maps 메뉴로 이동');
          console.error('3. Application 등록 또는 기존 Application 선택');
          console.error('4. Application 설정에서 다음 도메인 추가:');
          console.error('   - https://kidscafe.modouarena.com');
          console.error('   - http://kidscafe.modouarena.com');
          console.error('   - https://www.kidscafe.modouarena.com');
          console.error('   - http://www.kidscafe.modouarena.com');
          console.error('5. Web Service URL 섹션에 도메인 등록');
          console.error('6. Maps 서비스 활성화 확인');
          console.error('7. 설정 저장 후 1-2분 대기');
        } else if (isDevelopment) {
          console.error('🔧 개발 환경 설정 확인:');
          console.error('1. .env 파일의 VITE_NAVER_MAP_CLIENT_ID 확인');
          console.error('2. 네이버 클라우드 플랫폼 Maps 서비스에 localhost 도메인 등록 확인');
        } else {
          console.error('🔧 알 수 없는 도메인입니다. 네이버 클라우드 플랫폼 Maps 서비스에 등록이 필요합니다.');
        }
      };

      // 스크립트 생성 및 로드
      const script = document.createElement('script');
      script.type = 'text/javascript';
      // ncpClientId 사용 (올바른 파라미터)
      script.src = `https://oapi.map.naver.com/openapi/v3/maps.js?ncpClientId=${naverClientId}&submodules=geocoder&callback=initNaverMap`;
      script.async = true;
      script.defer = true;
      
      script.onerror = () => {
        console.error('❌ 네이버 지도 API 스크립트 로드 실패');
        console.error('네트워크 연결 상태를 확인하거나 브라우저 콘솔에서 자세한 오류를 확인하세요.');
      };
      
      document.head.appendChild(script);
      console.log('📍 네이버 지도 API 로드 시작...');
    };

    // 페이지 로드 후 실행
    if (document.readyState === 'complete') {
      loadNaverMapsAPI();
    } else {
      window.addEventListener('load', loadNaverMapsAPI);
    }

    return () => {
      window.removeEventListener('load', loadNaverMapsAPI);
    };
  }, []);

  const handleMenuClick = () => {
    setCurrentPage('menu');
    window.scrollTo(0, 0);
  };

  const handleFacilityClick = () => {
    setCurrentPage('facility');
    window.scrollTo(0, 0);
  };

  const handleNoticeClick = () => {
    setCurrentPage('notice');
    window.scrollTo(0, 0);
  };

  const handleBusinessClick = () => {
    setCurrentPage('business');
    window.scrollTo(0, 0);
  };

  const handleBusinessWithInquiry = (type: string) => {
    setInquiryType(type);
    setCurrentPage('business');
    // 페이지 이동 후 문의 폼으로 스크롤
    setTimeout(() => {
      const inquiryForm = document.getElementById('inquiry-form');
      if (inquiryForm) {
        inquiryForm.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const handleBusinessPageClick = () => {
    setCurrentPage('business');
    window.scrollTo(0, 0);
  };

  const handleBackToHome = () => {
    setCurrentPage('home');
    window.scrollTo(0, 0);
  };

  const handleTermsClick = () => {
    setCurrentPage('terms');
    window.scrollTo(0, 0);
  };

  const handlePrivacyClick = () => {
    setCurrentPage('privacy');
    window.scrollTo(0, 0);
  };

  // 기본적인 애니메이션 variants
  const fadeIn = {
    hidden: { 
      opacity: 0
    },
    visible: { 
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut" as const
      }
    }
  };

  const slideUp = {
    hidden: { 
      opacity: 0, 
      y: 40
    },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.7,
        ease: "easeOut" as const
      }
    }
  };

  const slideLeft = {
    hidden: { 
      opacity: 0, 
      x: -50
    },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: {
        duration: 0.7,
        ease: "easeOut" as const
      }
    }
  };

  const slideRight = {
    hidden: { 
      opacity: 0, 
      x: 50
    },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: {
        duration: 0.7,
        ease: "easeOut" as const
      }
    }
  };

  const scaleIn = {
    hidden: { 
      opacity: 0, 
      scale: 0.95
    },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut" as const
      }
    }
  };

  if (currentPage === 'menu') {
    return (
      <div className="min-h-screen bg-white">
        <Header 
          isHomePage={false}
          onMenuClick={handleMenuClick}
          onFacilityClick={handleFacilityClick}
          onNoticeClick={handleNoticeClick}
          onBusinessClick={handleBusinessClick}
          onHomeClick={handleBackToHome}
        />
        <Suspense fallback={<PageLoader />}>
          <MenuPage 
            onBackClick={handleBackToHome}
            onHomeClick={handleBackToHome}
            onFacilityClick={handleFacilityClick}
            onNoticeClick={handleNoticeClick}
            onBusinessClick={handleBusinessClick}
          />
        </Suspense>
      </div>
    );
  }

  if (currentPage === 'facility') {
    return (
      <div className="min-h-screen bg-white">
        <Header 
          isHomePage={false}
          onMenuClick={handleMenuClick}
          onFacilityClick={handleFacilityClick}
          onNoticeClick={handleNoticeClick}
          onBusinessClick={handleBusinessClick}
          onHomeClick={handleBackToHome}
        />
        <Suspense fallback={<PageLoader />}>
          <FacilityPage 
            onBackClick={handleBackToHome}
            onHomeClick={handleBackToHome}
            onMenuClick={handleMenuClick}
            onNoticeClick={handleNoticeClick}
            onBusinessClick={handleBusinessClick}
          />
        </Suspense>
      </div>
    );
  }

  if (currentPage === 'notice') {
    return (
      <div className="min-h-screen bg-white">
        <Header 
          isHomePage={false}
          onMenuClick={handleMenuClick}
          onFacilityClick={handleFacilityClick}
          onNoticeClick={handleNoticeClick}
          onBusinessClick={handleBusinessClick}
          onHomeClick={handleBackToHome}
        />
        <Suspense fallback={<PageLoader />}>
          <NoticePageWithAPI 
            onBackClick={handleBackToHome}
            onHomeClick={handleBackToHome}
            onFacilityClick={handleFacilityClick}
            onMenuClick={handleMenuClick}
            onBusinessClick={handleBusinessClick}
          />
        </Suspense>
      </div>
    );
  }

  if (currentPage === 'business') {
    return (
      <div className="min-h-screen bg-white">
        <Header 
          isHomePage={false}
          onMenuClick={handleMenuClick}
          onFacilityClick={handleFacilityClick}
          onNoticeClick={handleNoticeClick}
          onBusinessClick={handleBusinessClick}
          onHomeClick={handleBackToHome}
        />
        <Suspense fallback={<PageLoader />}>
          <BusinessPage 
            onBackClick={handleBackToHome}
            onHomeClick={handleBackToHome}
            onFacilityClick={handleFacilityClick}
            onMenuClick={handleMenuClick}
            onNoticeClick={handleNoticeClick}
            defaultInquiryType={inquiryType}
          />
        </Suspense>
      </div>
    );
  }

  if (currentPage === 'terms') {
    return (
      <div className="min-h-screen bg-white">
        <Suspense fallback={<PageLoader />}>
          <TermsPage onBackClick={handleBackToHome} />
        </Suspense>
      </div>
    );
  }

  if (currentPage === 'privacy') {
    return (
      <div className="min-h-screen bg-white">
        <Suspense fallback={<PageLoader />}>
          <PrivacyPage onBackClick={handleBackToHome} />
        </Suspense>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header - Fixed at top for home page */}
      <Header 
        isHomePage={true}
        onMenuClick={handleMenuClick}
        onFacilityClick={handleFacilityClick}
        onNoticeClick={handleNoticeClick}
        onBusinessClick={handleBusinessClick}
        onHomeClick={() => {}} // 홈페이지에서는 필요 없음
      />

      {/* Hero Section - 기본 fade in */}
      <motion.div
        variants={fadeIn}
        initial="hidden"
        animate="visible"
      >
        <div id="hero-section">
          <HeroSection />
        </div>
      </motion.div>

      {/* Facility Section - 왼쪽에서 슬라이드 */}
      <motion.div
        variants={slideLeft}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
      >
        <div id="facility-section">
          <FacilitySection onFacilityClick={handleFacilityClick} />
        </div>
      </motion.div>

      {/* Menu Section - 오른쪽에서 슬라이드 */}
      <motion.div
        variants={slideRight}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
      >
        <div id="menu-section">
          <MenuSection onMenuClick={handleMenuClick} />
        </div>
      </motion.div>

      {/* Usage Section - 아래에서 위로 */}
      <motion.div
        variants={slideUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
      >
        <div id="usage-section">
          <UsageSection />
        </div>
      </motion.div>

      {/* Notice Section - 살짝 확대하며 나타나기 */}
      <motion.div
        variants={scaleIn}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
      >
        <div id="notice-section">
          <NoticeSectionWithAPI onNoticePageClick={handleNoticeClick} />
        </div>
      </motion.div>

      {/* Business Section - 왼쪽에서 슬라이드 */}
      <motion.div
        variants={slideLeft}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        id="business-section"
      >
        <div id="business-section">
          <BusinessSection onBusinessPageClick={handleBusinessPageClick} />
        </div>
      </motion.div>

      {/* Contact Section - 내부에서 애니메이션 처리 */}
      <div id="contact-section">
        <ContactSection onBusinessClick={handleBusinessWithInquiry} />
      </div>

      {/* Footer - 기본 fade in */}
      <motion.div
        variants={fadeIn}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-50px" }}
      >
        <Footer 
          onTermsClick={handleTermsClick}
          onPrivacyClick={handlePrivacyClick}
        />
      </motion.div>
    </div>
  );
}